import { Injectable } from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { filter, first, map } from 'rxjs/operators'
import { Apollo } from 'apollo-angular'
import { LocalStorageService } from '@mistergreen/shared/angular-injectables/local-storage/local-storage.service'
import { AuthenticationService } from '../../../../framework/authentication/services/authentication.service'
import { gql } from '@apollo/client/core'

const localStorageKey = 'unconnectedCheckouts'

const connectProductConfiguration = gql`
    mutation connectDeal($id: ID!) {
        connectDeal(id: $id) {
            id
        }
    }
`

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class UnconnectedCheckoutService {
    checkoutMap: { [key: string]: { [key: string]: string } } = {}
    currentUserId: string

    constructor(
        private localStorageService: LocalStorageService,
        private authenticationService: AuthenticationService,
        private apollo: Apollo
    ) {
        // connect unconnected checkouts automatically when user is loged in
        this.authenticationService.currentUser
            .pipe(
                untilDestroyed(this),
                filter(() => this.authenticationService.isLoggedIn)
            )
            .subscribe(() => this.connectUnconnectedCheckouts())

        // get updates about the localstorage automatically
        this.localStorageService
            .getItemObservable(localStorageKey)
            .pipe(
                untilDestroyed(this),
                filter(value => !!value),
                map(value => JSON.parse(value as string))
            )
            .subscribe(checkoutMap => (this.checkoutMap = checkoutMap))
    }

    getCheckoutIdByMetaData(metaDataKey: string, metaDataValue: string): string | null {
        return (this.checkoutMap.hasOwnProperty(metaDataKey) && this.checkoutMap[metaDataKey][metaDataValue]) || null
    }

    storeCheckoutIdByMetaData(metaDataKey: string, metaDataValue: string, checkoutId: string) {
        this.checkoutMap[metaDataKey] = {
            ...(this.checkoutMap[metaDataKey] || {}),
            [metaDataValue]: checkoutId,
        }
        this.syncMapToStorage()
    }

    private syncMapToStorage() {
        this.localStorageService.setItem(localStorageKey, JSON.stringify(this.checkoutMap))
    }

    private clearMap() {
        // clear the storage
        this.checkoutMap = {}
        this.syncMapToStorage()
    }

    private connectUnconnectedCheckouts() {
        // get all checkout Ids
        const checkoutIds = Object.values(this.checkoutMap).reduce(
            (allCheckoutIds, metaDataCheckoutKeys) => [...allCheckoutIds, ...Object.values(metaDataCheckoutKeys)],
            []
        )

        this.clearMap()

        checkoutIds.forEach(id => {
            this.apollo
                .mutate({
                    mutation: connectProductConfiguration,
                    variables: { id },
                })
                .pipe(first())
                .subscribe()
        })
    }
}
